import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#D97757] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-stone-900">BeautyPro Review</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="font-heading text-4xl font-semibold text-stone-900 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-stone max-w-none">
          <p className="text-stone-600 mb-6"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">1. Information We Collect</h2>
            <p className="text-stone-700 leading-relaxed mb-4">We collect information you provide directly:</p>
            <ul className="list-disc pl-6 text-stone-700 space-y-2">
              <li><strong>Account Information:</strong> Name, email, phone number, business name, license type</li>
              <li><strong>Client Information:</strong> First name, last initial, and phone number of clients you review</li>
              <li><strong>Review Content:</strong> Ratings and written reviews you submit</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store card details)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-stone-700 space-y-2">
              <li>To provide and maintain our Service</li>
              <li>To process your subscription payments</li>
              <li>To display reviews to other verified professionals</li>
              <li>To communicate with you about your account</li>
              <li>To enforce our Terms of Service and review guidelines</li>
              <li>To improve our Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">3. Information Sharing</h2>
            <p className="text-stone-700 leading-relaxed mb-4">We share information only as follows:</p>
            <ul className="list-disc pl-6 text-stone-700 space-y-2">
              <li><strong>With Other Users:</strong> Reviews (including your name unless posted anonymously) are visible to other verified professionals</li>
              <li><strong>Service Providers:</strong> We use Stripe for payment processing and may use other providers for hosting and analytics</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
            </ul>
            <p className="text-stone-700 leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">4. Client Privacy</h2>
            <p className="text-stone-700 leading-relaxed">
              Client information (first name, last initial, phone) is shared only among verified beauty professionals 
              on our platform. This information is used solely to help professionals identify clients they may have 
              worked with. We encourage all users to respect client privacy and only share information directly 
              relevant to professional interactions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">5. Data Security</h2>
            <p className="text-stone-700 leading-relaxed">
              We implement appropriate security measures to protect your information. However, no method of 
              transmission over the Internet is 100% secure. We encourage you to use strong passwords and 
              protect your account credentials.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">6. Data Retention</h2>
            <p className="text-stone-700 leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide our Service. 
              You may request deletion of your account by contacting us. Some information may be retained as 
              required by law or for legitimate business purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">7. Your Rights</h2>
            <p className="text-stone-700 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-stone-700 space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your account</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">8. Cookies</h2>
            <p className="text-stone-700 leading-relaxed">
              We use cookies and similar technologies to maintain your session and improve your experience. 
              You can control cookies through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-stone-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              by posting a notice on our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-4">10. Contact Us</h2>
            <p className="text-stone-700 leading-relaxed">
              For questions about this Privacy Policy, contact us at{' '}
              <a href="mailto:info@beautyproreview.com" className="text-[#D97757] hover:underline">
                info@beautyproreview.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
